import React, { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";

function CreateSchedule({
  schedules,
  assignments,
  qaUsers,
  centers,
  priorities,
  onAddSchedule,
  onStartVisit,
  onCompleteVisit,
  onDeleteSchedule,
}) {
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [assignedCenters, setAssignedCenters] = useState([]);
  const { register, handleSubmit, reset, watch, setValue } = useForm();

  const watchQaUser = watch("qaUser");

  // Filter centers assigned to selected QA user
  useEffect(() => {
    if (watchQaUser) {
      const userAssignments = assignments.filter(a => String(a.qaUserId) === String(watchQaUser));
      const centerIds = userAssignments.map(a => String(a.centerId));
      const filteredCenters = centers.filter(c => centerIds.includes(String(c.id)));
      setAssignedCenters(filteredCenters);
      
      // Reset center selection
      setValue("center", "");
    } else {
      setAssignedCenters([]);
    }
  }, [watchQaUser, assignments, centers, setValue]);

  // Filter schedules by status
  const filteredSchedules = filterStatus === "ALL" 
    ? schedules 
    : schedules.filter(s => s.status === filterStatus);

  const handleFormSubmit = (data) => {
    const scheduleData = {
      qaUserId: data.qaUser,
      centerId: data.center,
      visitDate: data.visitDate,
      priority: data.priority,
      instructions: data.instructions,
      scheduledBy: localStorage.getItem("userId"),
      organizationId: localStorage.getItem("organizationId"),
    };

    if (onAddSchedule(scheduleData)) {
      reset();
      setValue("visitDate", new Date().toISOString().split('T')[0]);
    }
  };

  const getCenterName = useCallback((centerId) => {
    const center = centers.find(c => String(c.id) === String(centerId));
    return center ? center.name : "Unknown Center";
  }, [centers]);

  const getCityName = useCallback((centerId) => {
    const center = centers.find(c => String(c.id) === String(centerId));
    return center ? center.city : "Unknown City";
  }, [centers]);

  const getUserName = useCallback((userId) => {
    const user = qaUsers.find(u => String(u.id) === String(userId));
    return user ? `${user.firstName} ${user.lastName}` : "Unknown User";
  }, [qaUsers]);

  const getStatusBadge = (status) => {
    const styles = {
      Pending: "pill-pending",
      "In Progress": "pill-progress",
      Completed: "pill-completed",
      Missed: "pill-missed",
    };

    return (
      <span className={`pill ${styles[status] || ""}`} style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '.35rem',
        padding: '.18rem .65rem',
        borderRadius: '999px',
        fontSize: '.72rem',
        fontWeight: 700,
        backgroundColor: status === "Pending" ? '#fff3cd' : 
                        status === "In Progress" ? '#cfe2ff' : 
                        status === "Completed" ? '#d4edda' : 
                        status === "Missed" ? '#f8d7da' : '#f3f5f7',
        color: status === "Pending" ? '#856404' : 
               status === "In Progress" ? '#084298' : 
               status === "Completed" ? '#155724' : 
               status === "Missed" ? '#842029' : '#6c757d'
      }}>
        {status}
      </span>
    );
  };

  const handleViewDetails = (schedule) => {
    const center = centers.find(c => c.id === schedule.centerId);
    const message = `
      Schedule Details:
      ---------------
      ID: ${schedule.id}
      QA User: ${getUserName(schedule.qaUserId)}
      Center: ${center?.name || "N/A"} (${center?.city || "N/A"})
      Visit Date: ${schedule.visitDate}
      Priority: ${schedule.priority}
      Status: ${schedule.status}
      Instructions: ${schedule.instructions || "None"}
    `;
    alert(message);
  };

  return (
    <div className="row g-3">
      {/* Left Column - Form & Rules */}
      <div className="col-lg-4">
        {/* Schedule Form */}
        <div className="form-section" style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '1rem 1.25rem',
          background: 'white',
          marginBottom: '1rem'
        }}>
          <div className="form-section-title" style={{ 
            fontWeight: 700,
            fontSize: '.95rem',
            marginBottom: '.75rem'
          }}>
            Create Visit Schedule
          </div>
          
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="row g-2">
              <div className="col-12">
                <label className="form-label label-required" style={{ 
                  marginBottom: '0.5rem',
                  fontWeight: 500,
                  lineHeight: 1.2
                }}>
                  QA User <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <select
                  {...register("qaUser", { required: true })}
                  className="form-select form-select-sm"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '0.375rem 0.75rem',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    color: '#212529',
                    backgroundColor: '#fff',
                    backgroundClip: 'padding-box',
                    border: '1px solid #ced4da',
                    borderRadius: '0.25rem'
                  }}
                  required
                >
                  <option value="">Select QA User</option>
                  {qaUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-6">
                <label className="form-label label-required" style={{ 
                  marginBottom: '0.5rem',
                  fontWeight: 500,
                  lineHeight: 1.2
                }}>
                  Visit Date <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="date"
                  {...register("visitDate", { required: true })}
                  className="form-control form-control-sm"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '0.375rem 0.75rem',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    color: '#212529',
                    backgroundColor: '#fff',
                    backgroundClip: 'padding-box',
                    border: '1px solid #ced4da',
                    borderRadius: '0.25rem'
                  }}
                  required
                />
              </div>
              
              <div className="col-6">
                <label className="form-label" style={{ 
                  marginBottom: '0.5rem',
                  fontWeight: 500,
                  lineHeight: 1.2
                }}>
                  Priority
                </label>
                <select
                  {...register("priority")}
                  className="form-select form-select-sm"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '0.375rem 0.75rem',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    color: '#212529',
                    backgroundColor: '#fff',
                    backgroundClip: 'padding-box',
                    border: '1px solid #ced4da',
                    borderRadius: '0.25rem'
                  }}
                >
                  <option value="">Select Priority</option>
                  {priorities.map(p => (
                    <option key={p.id} value={p.value}>
                      {p.value}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12">
                <label className="form-label label-required" style={{ 
                  marginBottom: '0.5rem',
                  fontWeight: 500,
                  lineHeight: 1.2
                }}>
                  Center (Assigned) <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <select
                  {...register("center", { required: true })}
                  className="form-select form-select-sm"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '0.375rem 0.75rem',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    color: '#212529',
                    backgroundColor: '#fff',
                    backgroundClip: 'padding-box',
                    border: '1px solid #ced4da',
                    borderRadius: '0.25rem'
                  }}
                  required
                  disabled={assignedCenters.length === 0}
                >
                  <option value="">
                    {assignedCenters.length === 0 
                      ? "No centers assigned" 
                      : "Select center"}
                  </option>
                  {assignedCenters.map(center => (
                    <option key={center.id} value={center.id}>
                      {center.name} • {center.city}
                    </option>
                  ))}
                </select>
                <div className="small-helper mt-1" style={{ fontSize: '.75rem', color: '#6c757d' }}>
                  This list is filtered to centers assigned to the selected QA user
                </div>
              </div>

              <div className="col-12">
                <label className="form-label" style={{ 
                  marginBottom: '0.5rem',
                  fontWeight: 500,
                  lineHeight: 1.2
                }}>
                  Instructions
                </label>
                <textarea
                  {...register("instructions")}
                  rows="2"
                  className="form-control form-control-sm"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '0.375rem 0.75rem',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    color: '#212529',
                    backgroundColor: '#fff',
                    backgroundClip: 'padding-box',
                    border: '1px solid #ced4da',
                    borderRadius: '0.25rem'
                  }}
                  placeholder="Optional instructions (e.g., focus on labs, hygiene, teacher attendance)"
                />
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => reset()}
                className="btn btn-outline-secondary btn-sm"
              >
                <i className="fas fa-times-circle mr-2"></i>
                Clear
              </button>
              <button
                type="submit"
                className="btn btn-success btn-sm"
                disabled={assignedCenters.length === 0}
              >
                <i className="fas fa-calendar-plus mr-2"></i>
                Create Schedule
              </button>
            </div>
          </form>
        </div>

        {/* Rules Section */}
        <div className="form-section" style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '1rem 1.25rem',
          background: 'white',
          marginBottom: '1rem'
        }}>
          <div className="form-section-title" style={{ 
            fontWeight: 700,
            fontSize: '.95rem',
            marginBottom: '.75rem'
          }}>
            Rules (Check & Balance)
          </div>
          
          <div className="small">
            <div className="chip mb-2" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '.35rem',
              padding: '.25rem .55rem',
              border: '1px solid #e5e7eb',
              borderRadius: '999px',
              background: '#fff',
              fontSize: '.8rem',
              marginBottom: '0.5rem'
            }}>
              <i className="fas fa-map-marker-alt"></i> GPS is mandatory at visit start (mobile side)
            </div>
            <br/>
            <div className="chip mb-2" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '.35rem',
              padding: '.25rem .55rem',
              border: '1px solid #e5e7eb',
              borderRadius: '999px',
              background: '#fff',
              fontSize: '.8rem',
              marginBottom: '0.5rem'
            }}>
              <i className="fas fa-clock"></i> Time-In/Time-Out captured when visit starts/ends
            </div>
            <br/>
            <div className="chip mb-2" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '.35rem',
              padding: '.25rem .55rem',
              border: '1px solid #e5e7eb',
              borderRadius: '999px',
              background: '#fff',
              fontSize: '.8rem',
              marginBottom: '0.5rem'
            }}>
              <i className="fas fa-shield-alt"></i> QA form comes from SMS Portal (dynamic)
            </div>
            <br/>
            <div className="chip" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '.35rem',
              padding: '.25rem .55rem',
              border: '1px solid #e5e7eb',
              borderRadius: '999px',
              background: '#fff',
              fontSize: '.8rem'
            }}>
              <i className="fas fa-file-pdf"></i> Report PDF downloadable after completion
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Schedules Table */}
      <div className="col-lg-8">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0" style={{ fontSize: '1rem', fontWeight: 600 }}>
            Scheduled Visits
          </h6>
          
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted" style={{ fontSize: '.875rem' }}>Status:</span>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="form-select form-select-sm"
              style={{ width: '170px' }}
            >
              <option value="ALL">All</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Missed">Missed</option>
            </select>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th style={{ width: '42px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>#</th>
                <th style={{ width: '120px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>Schedule ID</th>
                <th style={{ width: '160px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>QA User</th>
                <th style={{ fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>Center</th>
                <th style={{ width: '140px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>City</th>
                <th style={{ width: '130px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>Visit Date</th>
                <th style={{ width: '120px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>Status</th>
                <th style={{ width: '90px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>Actions</th>
              </tr>
            </thead>
            
            <tbody>
              {filteredSchedules.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    No schedules found
                  </td>
                </tr>
              ) : (
                filteredSchedules.map((schedule, index) => (
                  <tr key={schedule.id}>
                    <td style={{ fontSize: '.875rem', color: '#6c757d' }}>
                      {index + 1}
                    </td>
                    <td style={{ 
                      fontSize: '.875rem', 
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                    }}>
                      {schedule.id}
                    </td>
                    <td style={{ fontSize: '.875rem', fontWeight: 500 }}>
                      {getUserName(schedule.qaUserId)}
                    </td>
                    <td style={{ fontSize: '.875rem' }}>
                      {getCenterName(schedule.centerId)}
                    </td>
                    <td style={{ fontSize: '.875rem', color: '#6c757d' }}>
                      {getCityName(schedule.centerId)}
                    </td>
                    <td style={{ 
                      fontSize: '.875rem', 
                      color: '#6c757d',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                    }}>
                      {schedule.visitDate}
                    </td>
                    <td>
                      {getStatusBadge(schedule.status)}
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          onClick={() => handleViewDetails(schedule)}
                          className="btn btn-outline-secondary"
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        
                        <button
                          onClick={() => onStartVisit(schedule.id)}
                          disabled={schedule.status !== "Pending"}
                          className={`btn ${schedule.status === "Pending" ? "btn-outline-primary" : "btn-outline-secondary"}`}
                          title="Start Visit"
                        >
                          <i className="fas fa-play-circle"></i>
                        </button>
                        
                        <button
                          onClick={() => onCompleteVisit(schedule.id)}
                          disabled={schedule.status !== "In Progress"}
                          className={`btn ${schedule.status === "In Progress" ? "btn-outline-success" : "btn-outline-secondary"}`}
                          title="Complete Visit"
                        >
                          <i className="fas fa-check-circle"></i>
                        </button>
                        
                        <button
                          onClick={() => {
                            if (window.confirm("Are you sure you want to delete this schedule?")) {
                              onDeleteSchedule(schedule.id);
                            }
                          }}
                          className="btn btn-outline-danger"
                          title="Delete Schedule"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="small-helper mt-2" style={{ fontSize: '.75rem', color: '#6c757d' }}>
          <i className="fas fa-info-circle mr-1"></i>
          Note: In real system, "Start Visit" is done in the mobile app. Here it is simulated for workflow preview.
        </div>
      </div>
    </div>
  );
}

export default CreateSchedule;